import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fmtDate, fmtDateTime } from "../lib/api";
import { Surface, Btn, Input, Select, Textarea } from "./Primitives";
import {
  Plus, Trash, UploadSimple, ChatCircleDots, PaperPlaneTilt,
  FileText, BookOpen, ArrowClockwise, Sparkle, Quotes,
} from "@phosphor-icons/react";

const KIND_LABELS = {
  poh: "POH",
  afm: "AFM",
  maintenance_manual: "Maintenance",
  ipc: "IPC",
  service_bulletin: "SB",
  checklist: "Checklist",
  weight_balance: "W&B",
  other: "Other",
};

const STATUS_BADGE = {
  queued:    { label: "QUEUED",    cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600" },
  parsing:   { label: "PARSING",   cls: "bg-amber/20 text-amber border-amber/60" },
  embedding: { label: "INDEXING",  cls: "bg-cyan-500/20 text-cyan-200 border-cyan-500/60" },
  ready:     { label: "READY",     cls: "bg-emerald-500/20 text-emerald-200 border-emerald-500/60" },
  failed:    { label: "FAILED",    cls: "bg-red-500/20 text-red-200 border-red-500/60" },
};

function StatusPill({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.queued;
  return (
    <span
      className={`tag font-mono text-[10px] tracking-wider border ${s.cls}`}
      style={{ padding: "2px 8px" }}
      data-testid={`manual-status-${status}`}
    >
      {s.label}
    </span>
  );
}

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ────────────────────────────────────────────────────────────────────────────
// Upload modal
// ────────────────────────────────────────────────────────────────────────────
function UploadModal({ open, onClose, aircraftId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("poh");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setFile(null); setKind("poh"); setTitle(""); setError(""); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) { setError("Pick a PDF file first."); return; }
    if (file.size > 100 * 1024 * 1024) { setError("Max 100 MB per file."); return; }
    if (!file.type.startsWith("application/pdf")) { setError("PDFs only."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      if (title) form.append("title", title);
      const r = await api.post(`/aircraft/${aircraftId}/manuals`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded?.(r.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="upload-manual-modal">
      <Surface className="w-full max-w-lg p-6">
        <div className="text-xs uppercase tracking-[0.25em] text-amber font-mono mb-4 flex items-center gap-2">
          <UploadSimple size={14} /> Upload aircraft manual
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">PDF file</div>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-zinc-200 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-amber/20 file:text-amber file:font-mono file:text-xs hover:file:bg-amber/30"
              data-testid="upload-manual-file"
              required
            />
            {file && <div className="mt-1.5 text-xs text-zinc-500 font-mono">{file.name} · {formatBytes(file.size)}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Manual type</div>
            <Select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="upload-manual-kind">
              {Object.entries(KIND_LABELS).map(([k, lbl]) => (
                <option key={k} value={k}>{lbl}</option>
              ))}
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Display title (optional)</div>
            <Input
              placeholder="e.g. 172N POH (rev. 5)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="upload-manual-title"
            />
          </div>
          {error && <div className="text-sm text-red-400" data-testid="upload-manual-error">{error}</div>}
          <div className="text-[11px] text-zinc-500 leading-relaxed">
            The PDF will be parsed, chunked, and embedded for retrieval. Large
            manuals (100&nbsp;+ pages) can take 30–90 seconds to index.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="ghost" onClick={onClose} data-testid="upload-manual-cancel">Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="upload-manual-submit">
              {busy ? "Uploading…" : "Upload & ingest"}
            </Btn>
          </div>
        </form>
      </Surface>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// One assistant/user message bubble
// ────────────────────────────────────────────────────────────────────────────
function CitationChip({ cite, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      className="inline-flex flex-col items-start gap-1 px-2 py-1 rounded border border-amber/40 bg-amber/10 text-amber text-[10px] font-mono hover:bg-amber/20 transition-colors max-w-full"
      data-testid={`citation-chip-${idx}`}
    >
      <span className="flex items-center gap-1">
        <Quotes size={10} />
        <span className="truncate">[{idx + 1}] {cite.filename} · p.{cite.page}</span>
      </span>
      {open && (
        <span className="block text-left text-[10px] text-zinc-300 font-sans normal-case tracking-normal whitespace-normal mt-1 max-w-md">
          {cite.snippet}{cite.snippet?.length >= 280 ? "…" : ""}
        </span>
      )}
    </button>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${msg.role}`}>
      <div className={`max-w-[80%] rounded-lg px-4 py-3 ${isUser ? "bg-amber/10 border border-amber/30" : "bg-zinc-900/60 border border-zinc-800"}`}>
        <div className="text-[10px] uppercase tracking-[0.2em] mb-1.5 font-mono text-zinc-500">
          {isUser ? "You" : "Assistant"} · {msg.created_at ? fmtDateTime(msg.created_at) : ""}
        </div>
        <div className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed">{msg.content}</div>
        {msg.citations?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {msg.citations.map((c, i) => <CitationChip key={`${c.chunk_id}-${i}`} cite={c} idx={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main AI Assistant tab
// ────────────────────────────────────────────────────────────────────────────
export default function AIAssistantTab({ aircraftId, isOwner, aircraft }) {
  const [manuals, setManuals] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [draft, setDraft] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedManuals, setSelectedManuals] = useState([]);   // empty → all
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const loadManuals = useCallback(async () => {
    const r = await api.get(`/aircraft/${aircraftId}/manuals`);
    setManuals(r.data);
    return r.data;
  }, [aircraftId]);

  const loadSessions = useCallback(async () => {
    const r = await api.get(`/aircraft/${aircraftId}/chat/sessions`);
    setSessions(r.data);
    return r.data;
  }, [aircraftId]);

  const loadSessionDetail = useCallback(async (sid) => {
    if (!sid) { setMessages([]); return; }
    const r = await api.get(`/aircraft/${aircraftId}/chat/sessions/${sid}`);
    setMessages(r.data.messages || []);
    setActiveSession(r.data.session);
  }, [aircraftId]);

  useEffect(() => {
    loadManuals();
    loadSessions();
  }, [loadManuals, loadSessions]);

  // Auto-poll status of any indexing manual every 3s until ready/failed
  useEffect(() => {
    const stillBusy = manuals.some((m) => ["queued", "parsing", "embedding"].includes(m.status));
    if (!stillBusy) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(() => { loadManuals(); }, 3000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [manuals, loadManuals]);

  // Auto-scroll chat to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  const readyManuals = useMemo(() => manuals.filter((m) => m.status === "ready"), [manuals]);
  const hasAny = readyManuals.length > 0;

  const toggleManual = (mid) => {
    setSelectedManuals((sel) =>
      sel.includes(mid) ? sel.filter((x) => x !== mid) : [...sel, mid]
    );
  };

  const newChat = () => {
    setActiveSession(null);
    setMessages([]);
    setDraft("");
    setError("");
  };

  const openSession = async (s) => {
    setDraft("");
    setError("");
    await loadSessionDetail(s.id);
  };

  const renameSession = async (s) => {
    const title = window.prompt("Rename chat", s.title || "");
    if (!title || title === s.title) return;
    await api.patch(`/aircraft/${aircraftId}/chat/sessions/${s.id}`, { title });
    await loadSessions();
    if (activeSession?.id === s.id) setActiveSession({ ...activeSession, title });
  };

  const deleteSession = async (s) => {
    if (!window.confirm(`Delete chat "${s.title}"?`)) return;
    await api.delete(`/aircraft/${aircraftId}/chat/sessions/${s.id}`);
    await loadSessions();
    if (activeSession?.id === s.id) {
      setActiveSession(null);
      setMessages([]);
    }
  };

  const deleteManual = async (m) => {
    if (!window.confirm(`Delete manual "${m.title || m.filename}"?\nThis removes ${m.chunk_count || 0} indexed chunks.`)) return;
    await api.delete(`/aircraft/${aircraftId}/manuals/${m.id}`);
    setSelectedManuals((sel) => sel.filter((x) => x !== m.id));
    await loadManuals();
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || pending) return;
    if (!hasAny) {
      setError("Upload at least one manual first so I have something to reference.");
      return;
    }
    setError("");
    setPending(true);
    // Optimistic user bubble
    const optimistic = { id: `temp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setDraft("");
    try {
      const body = {
        message: text,
        session_id: activeSession?.id,
        manual_ids: selectedManuals.length ? selectedManuals : undefined,
        top_k: 5,
      };
      const r = await api.post(`/aircraft/${aircraftId}/chat`, body);
      const { session, user_message, assistant_message, citations, ai_error } = r.data;
      // Replace optimistic user msg with the server's persisted one + append assistant msg
      setActiveSession(session);
      setMessages((m) => {
        const cleaned = m.filter((x) => x.id !== optimistic.id);
        return [...cleaned, user_message, { ...assistant_message, citations }];
      });
      if (ai_error) setError(`AI fallback: ${ai_error}`);
      await loadSessions();
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setError(err?.response?.data?.detail || "Chat failed");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-assistant-tab">
      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        aircraftId={aircraftId}
        onUploaded={async () => { await loadManuals(); }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── Left rail: Manuals + Sessions ── */}
        <div className="lg:col-span-4 space-y-4">
          {/* Manuals */}
          <Surface className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.25em] text-amber font-mono flex items-center gap-2">
                <BookOpen size={13} /> Manuals
              </div>
              {isOwner && (
                <Btn variant="ghost" onClick={() => setUploadOpen(true)} data-testid="upload-manual-btn">
                  <Plus size={12} className="inline mr-1" /> Upload
                </Btn>
              )}
            </div>
            {manuals.length === 0 && (
              <div className="text-xs text-zinc-500 leading-relaxed" data-testid="manuals-empty">
                No manuals yet. Upload a POH, AFM, or maintenance manual to start
                asking questions grounded in your aircraft's actual documentation.
              </div>
            )}
            <div className="space-y-1.5">
              {manuals.map((m) => {
                const selected = selectedManuals.includes(m.id);
                const isReady = m.status === "ready";
                return (
                  <div
                    key={m.id}
                    className={`group flex items-start gap-2 px-2 py-2 rounded border ${selected ? "border-amber bg-amber/5" : "border-zinc-800 hover:border-zinc-700"} transition-colors`}
                    data-testid={`manual-row-${m.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => isReady && toggleManual(m.id)}
                      disabled={!isReady}
                      className="flex-1 text-left disabled:opacity-60"
                      data-testid={`manual-select-${m.id}`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <FileText size={12} className="text-zinc-500" />
                        <span className="text-sm text-zinc-100 truncate font-medium">{m.title || m.filename}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500">
                        <span>{KIND_LABELS[m.kind] || m.kind}</span>
                        {m.page_count && <span>· {m.page_count}p</span>}
                        {m.chunk_count > 0 && <span>· {m.chunk_count} chunks</span>}
                        <StatusPill status={m.status} />
                      </div>
                      {m.status === "failed" && m.error && (
                        <div className="mt-1 text-[10px] text-red-300/80">{m.error}</div>
                      )}
                    </button>
                    {isOwner && (
                      <button
                        type="button"
                        onClick={() => deleteManual(m)}
                        className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`manual-delete-${m.id}`}
                        title="Delete manual"
                      >
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {readyManuals.length > 0 && selectedManuals.length > 0 && (
              <div className="mt-3 text-[10px] text-zinc-500 font-mono flex items-center justify-between">
                <span>Filtering to {selectedManuals.length} of {readyManuals.length}</span>
                <button
                  type="button"
                  onClick={() => setSelectedManuals([])}
                  className="text-amber hover:underline"
                  data-testid="clear-manual-filter"
                >
                  Clear filter
                </button>
              </div>
            )}
          </Surface>

          {/* Sessions */}
          <Surface className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.25em] text-amber font-mono flex items-center gap-2">
                <ChatCircleDots size={13} /> Chats
              </div>
              <Btn variant="ghost" onClick={newChat} data-testid="new-chat-btn">
                <Plus size={12} className="inline mr-1" /> New
              </Btn>
            </div>
            {sessions.length === 0 && (
              <div className="text-xs text-zinc-500" data-testid="sessions-empty">No conversations yet.</div>
            )}
            <div className="space-y-1">
              {sessions.map((s) => {
                const active = activeSession?.id === s.id;
                return (
                  <div
                    key={s.id}
                    className={`group flex items-start gap-2 px-2 py-1.5 rounded ${active ? "bg-amber/10 border border-amber/40" : "border border-transparent hover:border-zinc-800"}`}
                    data-testid={`session-row-${s.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => openSession(s)}
                      className="flex-1 text-left"
                      data-testid={`session-open-${s.id}`}
                    >
                      <div className="text-sm text-zinc-100 truncate">{s.title || "Untitled chat"}</div>
                      <div className="text-[10px] font-mono text-zinc-500">
                        {fmtDate(s.updated_at)} · {s.message_count || 0} msgs
                      </div>
                    </button>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button type="button" onClick={() => renameSession(s)} className="text-zinc-500 hover:text-amber text-[10px] font-mono uppercase tracking-wider" data-testid={`session-rename-${s.id}`}>
                        Rename
                      </button>
                      <button type="button" onClick={() => deleteSession(s)} className="text-zinc-500 hover:text-red-400" data-testid={`session-delete-${s.id}`} title="Delete">
                        <Trash size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Surface>
        </div>

        {/* ── Right pane: Chat thread ── */}
        <div className="lg:col-span-8">
          <Surface className="p-0 flex flex-col" style={{ minHeight: "70vh" }}>
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-amber font-mono flex items-center gap-2">
                  <Sparkle size={13} /> AI Assistant {aircraft?.registration && `· ${aircraft.registration}`}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {activeSession ? activeSession.title : "New chat"}
                </div>
              </div>
              <Btn variant="ghost" onClick={() => { loadManuals(); loadSessions(); if (activeSession?.id) loadSessionDetail(activeSession.id); }} data-testid="refresh-chat-btn">
                <ArrowClockwise size={12} className="inline" />
              </Btn>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="chat-thread">
              {messages.length === 0 && !pending && (
                <div className="h-full flex items-center justify-center text-center px-6 py-12" data-testid="chat-empty">
                  <div className="max-w-md">
                    <Sparkle size={28} className="mx-auto text-amber mb-4" />
                    <div className="text-sm text-zinc-300 mb-2">Ask anything about your aircraft.</div>
                    <div className="text-xs text-zinc-500 leading-relaxed">
                      I search your uploaded manuals and answer with page-level citations.
                      Try <span className="text-zinc-300">"What is Vy?"</span> or
                      <span className="text-zinc-300"> "Engine oil change interval"</span>.
                    </div>
                  </div>
                </div>
              )}
              {messages.map((m) => <MessageBubble key={m.id} msg={m} />)}
              {pending && (
                <div className="flex justify-start" data-testid="chat-thinking">
                  <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] mb-1.5 font-mono text-zinc-500">Assistant</div>
                    <div className="flex items-center gap-1.5 text-sm text-zinc-400">
                      <span className="inline-block w-1.5 h-1.5 bg-amber rounded-full animate-pulse" />
                      <span className="inline-block w-1.5 h-1.5 bg-amber rounded-full animate-pulse" style={{ animationDelay: "150ms" }} />
                      <span className="inline-block w-1.5 h-1.5 bg-amber rounded-full animate-pulse" style={{ animationDelay: "300ms" }} />
                      <span className="ml-2 text-xs">Searching manuals…</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="px-5 py-2 border-t border-red-500/30 bg-red-500/10 text-xs text-red-300" data-testid="chat-error">
                {error}
              </div>
            )}

            <form onSubmit={send} className="border-t border-zinc-800 px-5 py-4">
              <div className="flex items-end gap-2">
                <Textarea
                  rows={2}
                  placeholder={hasAny ? "Ask about procedures, limitations, performance, AD compliance…" : "Upload a manual first to start asking questions."}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); }
                  }}
                  disabled={pending}
                  data-testid="chat-input"
                  className="flex-1 resize-none"
                />
                <Btn type="submit" disabled={pending || !draft.trim() || !hasAny} data-testid="chat-send">
                  <PaperPlaneTilt size={14} className="inline" />
                </Btn>
              </div>
              <div className="mt-2 text-[10px] text-zinc-600 font-mono">
                Enter to send · Shift+Enter for newline
                {selectedManuals.length > 0 && ` · scoped to ${selectedManuals.length} manual${selectedManuals.length === 1 ? "" : "s"}`}
              </div>
            </form>
          </Surface>
        </div>
      </div>
    </div>
  );
}
